import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types-expenses';
import type { Expense, ExpenseInput } from './form-model';

/**
 * The problem codes the capture screens name in words. Every code the create and update routes
 * answer is here (#946); a 4xx with another code is still named by its status (`UNAUTHORIZED`,
 * `FORBIDDEN`, `REFUSED`), so «non risponde» (`OTHER`) is left for a 5xx or no answer at all.
 */
export const KNOWN_PROBLEMS = [
  'TOTAL_MISMATCH',
  'UNKNOWN_CATEGORY',
  'TAX_LINE_INVALID',
  'EXPECTED_DOCUMENT_NOTE_REQUIRED',
  'VERSION_CONFLICT',
  'NOT_A_PDF',
  'DOCUMENT_TOO_LARGE',
  'OWNER_NOT_IN_ORGANIZATION',
  'PEOPLE_UNAVAILABLE',
  'REPORT_NOT_AVAILABLE',
  'REPORT_NOT_EDITABLE',
  'EXPENSE_OUT_OF_PERIOD',
  'CATEGORY_NOT_ALLOWED_FOR_NOTE',
  'CATEGORY_RESERVED',
  'FIELDS_REQUIRED',
  'NEGATIVE_AMOUNT',
  'CARD_NOT_AVAILABLE',
  'VEHICLE_NOT_AVAILABLE',
  'SUPPLIER_NOT_FOUND',
  'OWNER_NOT_AVAILABLE',
  'PAYMENT_INCONSISTENT',
  'MILEAGE_TOTAL_MISMATCH',
  'MONTH_CLOSED',
  'EXPENSE_FORBIDDEN',
  'EXPENSE_NOT_FOUND',
  'CLASS_NOT_ALLOWED',
  'ADVANCE_NOT_HOLDER',
  'ADVANCE_METHOD_FORCED',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'REFUSED',
] as const;
export type KnownProblem = (typeof KNOWN_PROBLEMS)[number];

export type ApiOutcome<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly status: number; readonly code: KnownProblem | 'OTHER' };

/** A code the screens do not name, by its status: the caller's session, its rights, its input. */
function byStatus(status: number): KnownProblem | 'OTHER' {
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  if (status >= 400 && status <= 499) return 'REFUSED';
  return 'OTHER';
}

function problem(status: number, error: unknown): ApiOutcome<never> {
  const code =
    typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';
  if (status === 413) return { ok: false, status, code: 'DOCUMENT_TOO_LARGE' };
  return {
    ok: false,
    status,
    code: (KNOWN_PROBLEMS as readonly string[]).includes(code)
      ? (code as KnownProblem)
      : byStatus(status),
  };
}

async function call<T>(
  run: () => Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<ApiOutcome<T>> {
  try {
    const { data, error, response } = await run();
    if (response.ok && data !== undefined) return { ok: true, value: data };
    return problem(response.status, error);
  } catch {
    return { ok: false, status: 0, code: 'OTHER' };
  }
}

export type Person = components['schemas']['Person'];

/** The organization's people, for «Per conto di» (administrators only). */
export async function listPeople(): Promise<ApiOutcome<readonly Person[]>> {
  const outcome = await call(() => apiClient.GET('/v1/expenses/people'));
  return outcome.ok ? { ok: true, value: outcome.value.items } : outcome;
}

export function getExpense(expenseId: string): Promise<ApiOutcome<Expense>> {
  return call(() => apiClient.GET('/v1/expenses/{expenseId}', { params: { path: { expenseId } } }));
}

export function createExpense(body: ExpenseInput): Promise<ApiOutcome<Expense>> {
  return call(() => apiClient.POST('/v1/expenses', { body }));
}

export function updateExpense(
  expenseId: string,
  version: string,
  body: ExpenseInput,
): Promise<ApiOutcome<Expense>> {
  return call(() =>
    apiClient.PATCH('/v1/expenses/{expenseId}', {
      params: { path: { expenseId }, header: { 'If-Match': version } },
      body,
    }),
  );
}

/**
 * Attaches (or replaces) the expense's PDF as the raw request body (design §2.4). openapi-fetch
 * would JSON-encode a body, so the serializer hands the Blob over untouched.
 */
export function uploadDocument(
  expenseId: string,
  pdf: Blob,
  source: 'images' | 'pdf',
): Promise<ApiOutcome<Expense>> {
  return call(() =>
    apiClient.PUT('/v1/expenses/{expenseId}/document', {
      params: { path: { expenseId }, header: { 'X-Document-Source': source } },
      // The contract types the body as the PDF's bytes; a Blob is what fetch sends as such.
      body: pdf as unknown as string,
      bodySerializer: (body: unknown) => body as BodyInit,
      headers: { 'Content-Type': 'application/pdf' },
    }),
  );
}

export type Card = components['schemas']['Card'];
export type Vehicle = components['schemas']['Vehicle'];
export type Supplier = components['schemas']['Supplier'];

/** The owner's cards (`owner` undefined: the caller's), for «Come hai pagato?» with a card. */
export async function listCards(owner?: string): Promise<ApiOutcome<readonly Card[]>> {
  const outcome = await call(() =>
    apiClient.GET('/v1/expenses/cards', { params: { query: owner ? { owner } : {} } }),
  );
  return outcome.ok ? { ok: true, value: outcome.value.items } : outcome;
}

/** The owner's vehicles (`owner` undefined: the caller's), for mileage and fuel. */
export async function listVehicles(owner?: string): Promise<ApiOutcome<readonly Vehicle[]>> {
  const outcome = await call(() =>
    apiClient.GET('/v1/expenses/vehicles', { params: { query: owner ? { owner } : {} } }),
  );
  return outcome.ok ? { ok: true, value: outcome.value.items } : outcome;
}

/** Suppliers matching the text, by name or VAT number (at most 20). */
export async function searchSuppliers(q: string): Promise<ApiOutcome<readonly Supplier[]>> {
  const outcome = await call(() =>
    apiClient.GET('/v1/expenses/suppliers', { params: { query: { q } } }),
  );
  return outcome.ok ? { ok: true, value: outcome.value.items } : outcome;
}
